/**
 * LightChatContainer Component
 * Chat container with lazy initialization - no need to create chat upfront
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Spin, Alert, Button } from 'antd';
import { ArrowDownOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  useInfiniteGeneralChatMessages,
  useSendGeneralChatMessage,
  chatKeys,
} from '@/hooks/useChat';
import { useWebSocketStatus } from '@/context/WebSocketContext';
import { useAuth } from '@/context/AuthContext';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import type { Message } from '@/api/types/chat.types';
import {
  GENERAL_CHAT_INSTANCE_TYPE,
  GENERAL_CHAT_INSTANCE_ID,
} from '@/api/services/chat.service';
import './ChatContainer.css';

// Extended message type for local state management
interface LocalMessage extends Message {
  realId?: string; // Real ID from API (when temp message transitions)
}

interface LightChatContainerProps {
  /** Instance type for the chat (e.g., 'general', 'lead', 'ticket') */
  instanceType?: string;
  /** Instance ID for the chat (e.g., lead ID, ticket ID) */
  instanceId?: string;
  /** Chat title to display */
  title?: string;
  /** Hide the header (title + WebSocket status) */
  hideHeader?: boolean;
  /** Auto-focus input field on mount */
  autoFocus?: boolean;
}

export const LightChatContainer: React.FC<LightChatContainerProps> = ({
  instanceType = GENERAL_CHAT_INSTANCE_TYPE,
  instanceId = GENERAL_CHAT_INSTANCE_ID,
  title = 'General Chat',
  hideHeader = false,
  autoFocus = false,
}) => {
  const [localMessages, setLocalMessages] = useState<LocalMessage[]>([]);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Track component renders
  const renderCount = useRef(0);
  useEffect(() => {
    renderCount.current += 1;
    console.log(`🎨 [RENDER] Component render #${renderCount.current}`);
  });

  // Stable refs to prevent WebSocket listener re-registration
  const queryClientRef = useRef(queryClient);
  const instanceTypeRef = useRef(instanceType);
  const instanceIdRef = useRef(instanceId);

  // Update refs when values change (without triggering useEffect re-run)
  useEffect(() => {
    queryClientRef.current = queryClient;
    instanceTypeRef.current = instanceType;
    instanceIdRef.current = instanceId;
  });

  // Track localMessages changes
  useEffect(() => {
    console.log(
      '📊 [STATE CHANGE] localMessages changed, new count:',
      localMessages.length
    );
    console.log(
      '📊 [STATE CHANGE] localMessages:',
      localMessages.map((m) => ({ id: m.id, message: m.message }))
    );
  }, [localMessages]);

  // Fetch messages with infinite scroll (returns paginated response)
  const {
    data,
    isLoading: messagesLoading,
    error: messagesError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteGeneralChatMessages(30);

  // Send message mutation
  const sendMessageMutation = useSendGeneralChatMessage();

  // Use existing WebSocket connection from provider (shared instance!)
  const { socket, connected } = useWebSocketStatus();

  // Flatten all pages of messages from infinite query
  const allServerMessages = React.useMemo(() => {
    if (!data?.pages) return [];

    // Flatten all pages and reverse (backend returns oldest first, we want newest last)
    return data.pages
      .flatMap((page) => page.messages || [])
      .sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
  }, [data]);

  // Set up WebSocket event listeners
  useEffect(() => {
    if (!socket || !connected) {
      console.log('⏳ [WEBSOCKET] Waiting for WebSocket connection...');
      return;
    }

    console.log(
      '🔧 [WEBSOCKET] Registering chat event listeners (socket.id:',
      socket.id,
      ')'
    );
    console.log(
      '🔧 [WEBSOCKET] Instance:',
      instanceTypeRef.current,
      instanceIdRef.current
    );

    // Debug: Listen to ALL events to see what's coming in
    const handleAnyEvent = (...args: any[]) => {
      console.log('🔥 [WEBSOCKET] ANY EVENT RECEIVED:', args);
    };
    socket.onAny(handleAnyEvent);

    const handleMessageCreated = (eventData: any) => {
      console.log('🔌 [WEBSOCKET] Raw event received: chat.message.created');
      console.log('💬 [HANDLER] New message via WebSocket:', eventData);

      // Extract actual message data from nested structure
      const data = eventData.data || eventData;
      console.log('📦 [HANDLER] Extracted data:', {
        message_id: data?.message_id,
        message: data?.message,
        created_by: data?.created_by,
      });

      // Validate message data
      if (!data || !data.message_id) {
        console.error('❌ [HANDLER] Invalid message data:', eventData);
        return;
      }

      // Note: Backend doesn't send instance_type/instance_id in WebSocket events
      // WebSocket connection is already filtered by room subscription on backend
      console.log('✅ [HANDLER] Processing message for general chat...');
      setLocalMessages((prev) => {
        console.log('📝 [STATE] Current localMessages count:', prev.length);
        console.log('📝 [STATE] Looking for message with ID:', data.message_id);
        console.log(
          '📝 [STATE] All local messages:',
          prev.map((m) => ({
            id: m.id,
            realId: m.realId,
            message: m.message,
            isTempMessage: m.isTempMessage,
          }))
        );

        // Check if this message already exists (by ID or realId)
        const existingMessage = prev.find(
          (msg) => msg.id === data.message_id || msg.realId === data.message_id
        );

        if (existingMessage) {
          console.log('🔍 [STATE] Found existing message:', {
            id: existingMessage.id,
            realId: existingMessage.realId,
            isTempMessage: existingMessage.isTempMessage,
          });
          // If it's a temp message, replace it with real message (upgrade to delivered)
          if (existingMessage.isTempMessage) {
            console.log(
              `✓ [STATE] Upgrading temp message to delivered: ${existingMessage.id} → ${data.message_id}`
            );
            const updated = prev.map((msg) =>
              msg.id === existingMessage.id
                ? {
                    ...msg,
                    id: data.message_id, // Replace temp ID with real ID
                    status: 'delivered',
                    isTempMessage: false,
                    created_at: data.created_at, // Use real timestamp
                  }
                : msg
            );
            console.log(
              '📝 [STATE] After upgrade, localMessages count:',
              updated.length
            );
            return updated;
          } else {
            // Already exists as real message - ignore duplicate
            console.warn(
              `⚠️ [STATE] Duplicate message ignored: ${data.message_id}`
            );
            return prev;
          }
        } else {
          console.log(
            '🔍 [STATE] No existing message found with ID:',
            data.message_id
          );
        }

        console.log(
          `➕ [STATE] Adding NEW message from WebSocket: ${data.message_id}`,
          data
        );

        // Add new message directly to localMessages (will be shown immediately)
        const newMessage: LocalMessage = {
          id: data.message_id,
          chat_id: data.chat_id,
          message: data.message,
          created_by: data.created_by,
          created_by_user: data.created_by_user,
          created_at: data.created_at,
          parent_message_id: data.parent_message_id,
          has_attachments: data.has_attachments,
          attachments_metadata: data.attachments_metadata,
          tenant_id: data.tenant_id || '',
          updated_at: data.created_at,
          is_active: true,
          status: 'delivered',
          isTempMessage: false, // This is a real message from server
        };

        const updated = [...prev, newMessage];
        console.log(
          '📝 [STATE] After adding message, localMessages count:',
          updated.length
        );
        console.log(
          '📝 [STATE] New localMessages state:',
          updated.map((m) => ({ id: m.id, message: m.message }))
        );
        return updated;
      });
    };

    const handleMessageUpdated = (eventData: any) => {
      console.log('✏️ Message updated via WebSocket:', eventData);
      const data = eventData.data || eventData;
      setLocalMessages((prev) =>
        prev.map((msg) =>
          msg.id === data.message_id
            ? { ...msg, message: data.message, is_edited: true }
            : msg
        )
      );
    };

    const handleMessageArchived = (eventData: any) => {
      console.log('🗑️ Message archived via WebSocket:', eventData);
      const data = eventData.data || eventData;
      setLocalMessages((prev) =>
        prev.filter((msg) => msg.id !== data.message_id)
      );
    };

    const handleLikeAdded = (eventData: any) => {
      console.log('👍 Like added via WebSocket:', eventData);
      const data = eventData.data || eventData;
      queryClientRef.current.invalidateQueries({
        queryKey: chatKeys.likes(data.message_id),
      });
    };

    const handleLikeRemoved = (eventData: any) => {
      console.log('👎 Like removed via WebSocket:', eventData);
      const data = eventData.data || eventData;
      queryClientRef.current.invalidateQueries({
        queryKey: chatKeys.likes(data.message_id),
      });
    };

    const handleMessageSeen = (eventData: any) => {
      console.log('👁️ Message seen via WebSocket:', eventData);
      const data = eventData.data || eventData;
    };

    // Register event listeners
    socket.on('chat.message.created', handleMessageCreated);
    socket.on('chat.message.updated', handleMessageUpdated);
    socket.on('chat.message.archived', handleMessageArchived);
    socket.on('chat.like.added', handleLikeAdded);
    socket.on('chat.like.removed', handleLikeRemoved);
    socket.on('chat.message.seen', handleMessageSeen);

    // Cleanup function
    return () => {
      console.log(
        '🧹 [WEBSOCKET] Unregistering chat event listeners (socket.id:',
        socket.id,
        ')'
      );
      socket.offAny(handleAnyEvent);
      socket.off('chat.message.created', handleMessageCreated);
      socket.off('chat.message.updated', handleMessageUpdated);
      socket.off('chat.message.archived', handleMessageArchived);
      socket.off('chat.like.added', handleLikeAdded);
      socket.off('chat.like.removed', handleLikeRemoved);
      socket.off('chat.message.seen', handleMessageSeen);
    };
  }, [socket, connected]); // ✅ Removed instanceType, instanceId, queryClient - using refs instead!

  // Combine server messages with local messages (deduplicating)
  const messages = React.useMemo(() => {
    console.log('🔄 [MEMO] Combining messages...');
    console.log('🔄 [MEMO] Server messages count:', allServerMessages.length);
    console.log('🔄 [MEMO] Local messages count:', localMessages.length);
    console.log(
      '🔄 [MEMO] Server messages:',
      allServerMessages.map((m) => ({ id: m.id, message: m.message }))
    );
    console.log(
      '🔄 [MEMO] Local messages:',
      localMessages.map((m) => ({
        id: m.id,
        message: m.message,
        isTempMessage: m.isTempMessage,
      }))
    );

    const messageMap = new Map<string, Message>();

    // Add all server messages first
    allServerMessages.forEach((msg) => {
      messageMap.set(msg.id, msg);
    });
    console.log(
      '🔄 [MEMO] After adding server messages, map size:',
      messageMap.size
    );

    // Add local messages (will override server messages if they have the same ID)
    localMessages.forEach((localMsg) => {
      // Check if this message already exists in server data by ID or realId
      const existsInServer = allServerMessages.some(
        (serverMsg) =>
          serverMsg.id === localMsg.id ||
          (localMsg.realId && serverMsg.id === localMsg.realId)
      );

      if (!existsInServer) {
        // Only add if not in server data yet
        console.log(
          '🔄 [MEMO] Adding local message to map:',
          localMsg.id,
          localMsg.message
        );
        messageMap.set(localMsg.id, localMsg);
      } else if (localMsg.isTempMessage && localMsg.realId) {
        // If it's a temp message that has been confirmed, use the server version
        // (already in map with realId)
        console.log(
          '🔄 [MEMO] Skipping temp message (confirmed):',
          localMsg.id
        );
      } else {
        console.log(
          '🔄 [MEMO] Skipping local message (exists in server):',
          localMsg.id
        );
      }
    });

    console.log(
      '🔄 [MEMO] After adding local messages, map size:',
      messageMap.size
    );

    // Convert map to sorted array
    const result = Array.from(messageMap.values()).sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    console.log('🔄 [MEMO] Final combined messages count:', result.length);
    console.log(
      '🔄 [MEMO] Final messages:',
      result.map((m) => ({ id: m.id, message: m.message }))
    );

    return result;
  }, [allServerMessages, localMessages]);

  // Track messages changes (combined)
  useEffect(() => {
    console.log(
      '📊 [STATE CHANGE] Combined messages changed, new count:',
      messages.length
    );
  }, [messages]);

  // Load more messages (older)
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Handle scroll position changes
  const handleScroll = useCallback((isAtBottom: boolean) => {
    setShowScrollToBottom(!isAtBottom);
  }, []);

  // Handle send message with status tracking
  const handleSendMessage = async (messageText: string) => {
    console.log('💬 [SEND] User sending message:', messageText);
    if (!messageText.trim()) return;

    const tempId = `temp-${Date.now()}`;
    console.log('💬 [SEND] Generated temp ID:', tempId);

    try {
      // 1. Optimistic update - add message immediately with "sending" status
      const optimisticMessage: LocalMessage = {
        id: tempId,
        chat_id: 'pending',
        message: messageText,
        created_by: user?.id || 'unknown', // Use real user ID so message appears on correct side
        created_by_user: user
          ? {
              id: user.id,
              display_name:
                `${user.first_name} ${user.last_name}`.trim() || user.email,
              email: user.email,
              avatar_url: null,
            }
          : null,
        created_at: new Date().toISOString(),
        has_attachments: false,
        tenant_id: '',
        updated_at: new Date().toISOString(),
        is_active: true,
        status: 'sending', // ⏳ Status indicator
        isTempMessage: true, // Mark as temp for later replacement
      };

      console.log('💬 [SEND] Adding optimistic message to localMessages');
      setLocalMessages((prev) => {
        const updated = [...prev, optimisticMessage];
        console.log(
          '💬 [SEND] LocalMessages after optimistic add:',
          updated.length
        );
        return updated;
      });

      // 2. Send message to API (will create chat if doesn't exist)
      console.log('💬 [SEND] Sending message to API...');
      const response = await sendMessageMutation.mutateAsync({
        message: messageText,
      });
      console.log('💬 [SEND] API response received, message ID:', response.id);

      // 3. Replace temp message with real message immediately
      // Don't wait for WebSocket - backend confirmed it was saved
      console.log('💬 [SEND] Replacing temp message with real message');
      console.log(
        '💬 [SEND] Mapping tempId:',
        tempId,
        '→ realId:',
        response.id
      );
      setLocalMessages((prev) => {
        const updated = prev.map((msg) =>
          msg.id === tempId
            ? {
                ...msg,
                id: response.id, // Replace temp ID with real ID immediately
                status: 'delivered', // Mark as delivered (backend confirmed)
                isTempMessage: false, // No longer a temp message
                realId: undefined, // Clear realId (not needed anymore)
              }
            : msg
        );
        console.log(
          '💬 [SEND] After replacing with real message, messages:',
          updated.map((m) => ({
            id: m.id,
            realId: m.realId,
            message: m.message,
            isTempMessage: m.isTempMessage,
          }))
        );
        return updated;
      });
      console.log(
        '💬 [SEND] Message replaced - WebSocket event will be ignored as duplicate'
      );

      // 4. Real message will come via WebSocket with status "delivered" (✓✓)
      // WebSocket handler will match by realId and replace temp message
    } catch (error) {
      console.error('Failed to send message:', error);

      // Mark message as failed (⚠️) with retry option
      setLocalMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempId ? { ...msg, status: 'failed' } : msg
        )
      );
    }
  };

  // Loading state
  if (messagesLoading) {
    return (
      <div className="chat-container chat-container-loading">
        <Spin size="large" tip="Loading chat..." />
      </div>
    );
  }

  // Error state
  if (messagesError) {
    return (
      <div className="chat-container chat-container-error">
        <Alert
          message="Error Loading Chat"
          description="Failed to load messages. Please try again."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="chat-container">
      {/* Chat Header - Conditionally rendered */}
      {!hideHeader && (
        <div className="chat-header">
          <div className="chat-header-info">
            <h2 className="chat-title">{title}</h2>
            <p className="chat-description">
              {messages.length === 0
                ? 'No messages yet. Start the conversation!'
                : `${messages.length} messages`}
            </p>
          </div>
          {/* WebSocket status indicator */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
            }}
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: connected ? '#52c41a' : '#ff4d4f',
                display: 'inline-block',
              }}
            />
            <span style={{ color: '#666' }}>
              WebSocket: {connected ? 'Connected' : 'Disconnected'}
              {socket && ` (${socket.id?.substring(0, 8)}...)`}
            </span>
          </div>
        </div>
      )}

      {/* Messages List */}
      <MessageList
        messages={messages}
        chatId={`${instanceType}-${instanceId}`}
        onLoadMore={handleLoadMore}
        onScroll={handleScroll}
        isLoadingMore={isFetchingNextPage}
        hasMoreMessages={hasNextPage}
        onRetryMessage={(messageId) => {
          // Find the failed message and retry sending
          const failedMessage = messages.find((msg) => msg.id === messageId);
          if (failedMessage) {
            // Remove the failed message
            setLocalMessages((prev) =>
              prev.filter((msg) => msg.id !== messageId)
            );
            // Retry sending
            handleSendMessage(failedMessage.message);
          }
        }}
      />

      {/* Scroll to bottom button */}
      {showScrollToBottom && (
        <Button
          className="chat-scroll-to-bottom"
          shape="circle"
          icon={<ArrowDownOutlined />}
          onClick={() => {
            const messageList = document.querySelector('.message-list');
            if (messageList) {
              messageList.scrollTop = messageList.scrollHeight;
            }
          }}
        />
      )}

      {/* Message Input */}
      <MessageInput
        onSendMessage={handleSendMessage}
        disabled={sendMessageMutation.isPending}
        loading={sendMessageMutation.isPending}
        placeholder={
          connected
            ? 'Type a message...'
            : 'Type a message... (REST API mode - refresh to see replies)'
        }
        autoFocus={autoFocus}
      />
    </div>
  );
};
